import { SUPPORTED_TIMEZONES } from '../types/settings';

/**
 * 时区转换工具类
 */
export class TimezoneUtils {
  /**
   * 将UTC时间转换为指定时区的时间
   * @param utcTime UTC时间字符串或Date对象
   * @param timezone 目标时区 (如: 'Asia/Shanghai')
   * @returns 转换后的Date对象
   */
  static convertToTimezone(utcTime: string | Date, timezone: string): Date {
    const utcDate = typeof utcTime === 'string' ? new Date(utcTime) : utcTime;

    // 获取UTC时间戳
    const utcTimestamp = utcDate.getTime();

    // 获取目标时区的偏移量（分钟）
    const timezoneOffset = this.getTimezoneOffset(timezone);

    // 转换到目标时区
    const targetTimestamp = utcTimestamp + (timezoneOffset * 60 * 1000);

    return new Date(targetTimestamp);
  }

  /**
   * 将本地时间转换为UTC时间
   * @param localTime 本地时间字符串或Date对象
   * @param timezone 本地时区 (如: 'Asia/Shanghai')
   * @returns UTC时间的Date对象
   */
  static convertToUTC(localTime: string | Date, timezone: string): Date {
    const localDate = typeof localTime === 'string' ? new Date(localTime) : localTime;

    // 获取本地时间戳
    const localTimestamp = localDate.getTime();

    // 获取时区偏移量（分钟）
    const timezoneOffset = this.getTimezoneOffset(timezone);

    // 转换到UTC
    const utcTimestamp = localTimestamp - (timezoneOffset * 60 * 1000);

    return new Date(utcTimestamp);
  }

  /**
   * 获取时区的偏移量（分钟）
   * @param timezone 时区代码
   * @returns 偏移量（分钟）
   */
  static getTimezoneOffset(timezone: string): number {
    const timezoneOption = SUPPORTED_TIMEZONES.find(tz => tz.code === timezone);
    if (timezoneOption) {
      return timezoneOption.utcOffset * 60; // 转换为分钟
    }

    // 默认返回0（UTC）
    return 0;
  }

  /**
   * 格式化时间显示
   * @param utcTime UTC时间
   * @param timezone 目标时区
   * @param format 格式 ('date' | 'time' | 'datetime')
   * @param locale 语言环境
   * @returns 格式化后的时间字符串
   */
  static formatTime(
    utcTime: string | Date,
    timezone: string,
    format: 'date' | 'time' | 'datetime' = 'datetime',
    locale: string = 'zh-CN'
  ): string {
    const targetDate = this.convertToTimezone(utcTime, timezone);

    const options: Intl.DateTimeFormatOptions = {};

    switch (format) {
      case 'date':
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        break;
      case 'time':
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        break;
      case 'datetime':
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        break;
    }

    return new Intl.DateTimeFormat(locale, options).format(targetDate);
  }

  /**
   * 获取相对时间描述
   * @param utcTime UTC时间
   * @param timezone 目标时区
   * @param locale 语言环境
   * @returns 相对时间字符串
   */
  static getRelativeTime(
    utcTime: string | Date,
    timezone: string,
    locale: string = 'zh-CN'
  ): string {
    const targetDate = this.convertToTimezone(utcTime, timezone);
    const now = new Date();
    const diffInMs = now.getTime() - targetDate.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return this.getLocalizedText('time.justNow', locale);
    } else if (diffInMinutes < 60) {
      return this.getLocalizedText('time.minutesAgo', locale, { count: diffInMinutes });
    } else if (diffInHours < 24) {
      return this.getLocalizedText('time.hoursAgo', locale, { count: diffInHours });
    } else if (diffInDays < 30) {
      return this.getLocalizedText('time.daysAgo', locale, { count: diffInDays });
    } else if (diffInDays < 365) {
      const diffInMonths = Math.floor(diffInDays / 30);
      return this.getLocalizedText('time.monthsAgo', locale, { count: diffInMonths });
    } else {
      const diffInYears = Math.floor(diffInDays / 365);
      return this.getLocalizedText('time.yearsAgo', locale, { count: diffInYears });
    }
  }

  /**
   * 获取本地化的文本
   * @param key 翻译键
   * @param locale 语言环境
   * @param params 参数
   * @returns 本地化文本
   */
  private static getLocalizedText(key: string, locale: string, params?: Record<string, any>): string {
    // 这里应该从i18n系统中获取翻译
    // 暂时返回英文默认值
    const translations: Record<string, Record<string, string>> = {
      'time.justNow': { 'zh-CN': '刚刚', 'en-US': 'Just now' },
      'time.minutesAgo': { 'zh-CN': '{{count}} 分钟前', 'en-US': '{{count}} minutes ago' },
      'time.hoursAgo': { 'zh-CN': '{{count}} 小时前', 'en-US': '{{count}} hours ago' },
      'time.daysAgo': { 'zh-CN': '{{count}} 天前', 'en-US': '{{count}} days ago' },
      'time.monthsAgo': { 'zh-CN': '{{count}} 月前', 'en-US': '{{count}} months ago' },
      'time.yearsAgo': { 'zh-CN': '{{count}} 年前', 'en-US': '{{count}} years ago' },
    };

    const lang = translations[key]?.[locale] || translations[key]?.['en-US'] || key;

    if (params) {
      return Object.keys(params).reduce((str, param) => {
        return str.replace(new RegExp(`{{${param}}}`, 'g'), String(params[param]));
      }, lang);
    }

    return lang;
  }

  /**
   * 获取当前用户的时区
   * @returns 时区代码
   */
  static getCurrentTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      // 如果无法获取时区，返回默认时区
      return 'Asia/Shanghai';
    }
  }

  /**
   * 获取当前用户的语言
   * @returns 语言代码
   */
  static getCurrentLanguage(): string {
    try {
      const lang = navigator.language;
      // 检查是否支持该语言
      const supportedLanguages = ['zh-CN', 'zh-TW', 'en-US', 'ja-JP', 'ko-KR'];
      return supportedLanguages.includes(lang) ? lang : 'zh-CN';
    } catch {
      return 'zh-CN';
    }
  }
}

/**
 * 便捷的时区转换函数
 */
export const toTimezone = (utcTime: string | Date, timezone: string): Date => {
  return TimezoneUtils.convertToTimezone(utcTime, timezone);
};

export const toUTC = (localTime: string | Date, timezone: string): Date => {
  return TimezoneUtils.convertToUTC(localTime, timezone);
};

export const formatTime = (
  utcTime: string | Date,
  timezone: string,
  format: 'date' | 'time' | 'datetime' = 'datetime',
  locale: string = 'zh-CN'
): string => {
  return TimezoneUtils.formatTime(utcTime, timezone, format, locale);
};

export const getRelativeTime = (
  utcTime: string | Date,
  timezone: string,
  locale: string = 'zh-CN'
): string => {
  return TimezoneUtils.getRelativeTime(utcTime, timezone, locale);
};
